import type { ComputedRef, Ref } from 'vue'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { GlobalSearchResult } from '@lib/components/CGlobalSearch/CGlobalSearch.types'

interface VueElement extends HTMLElement {
  $el?: HTMLElement
}

const ARROW_UP = 'ArrowUp'
const ARROW_DOWN = 'ArrowDown'
const SLASH_KEY_CODE = 'Slash'

const KEY_DOWN_EVENT = 'keydown'
const KEY_UP_EVENT = 'keyup'

export const useShortcuts = (items: ComputedRef<GlobalSearchResult[]>, searchRef: Ref<VueElement | null>) => {
  const selectedResult: Ref<GlobalSearchResult | null> = ref(null)

  const selectElement = (item: GlobalSearchResult) => {
    const searchElement = document.querySelector(`[data-search-result-id="${item.id}"]`) as HTMLElement | null
    searchElement?.focus()
    selectedResult.value = item
  }

  const getIndexOfSelectedElement = (): number =>
    selectedResult.value ? items.value.indexOf(selectedResult.value) : -1

  const selectElementByIndex = (index: number) => {
    if (index === -1) {
      focusSearch()
    } else if (index >= 0 && index < items.value.length) {
      selectElement(items.value[index])
    }
  }

  const selectNextElement = () => selectElementByIndex(getIndexOfSelectedElement() + 1)
  const selectPreviousElement = () => selectElementByIndex(getIndexOfSelectedElement() - 1)

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.code === ARROW_UP) {
      event.preventDefault()
      selectPreviousElement()
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === ARROW_DOWN) {
      event.preventDefault()
      selectNextElement()
    }
  }

  watch(
    items,
    () => {
      selectedResult.value = null
    },
    { immediate: true },
  )

  const focusSearchOnSlash = (event: KeyboardEvent) => {
    if (event.code === SLASH_KEY_CODE) {
      focusSearch()
    }
  }

  const focusSearch = () => {
    selectedResult.value = null
    searchRef.value?.$el?.querySelector('input')?.focus()
  }

  onMounted(() => {
    document.addEventListener(KEY_UP_EVENT, focusSearchOnSlash)
    document.addEventListener(KEY_DOWN_EVENT, onKeyUp)
    document.addEventListener(KEY_DOWN_EVENT, onKeyDown)
  })

  onUnmounted(() => {
    document.removeEventListener(KEY_UP_EVENT, focusSearchOnSlash)
    document.removeEventListener(KEY_DOWN_EVENT, onKeyUp)
    document.removeEventListener(KEY_DOWN_EVENT, onKeyDown)
  })

  return {
    selectElement,
    selectedResult,
  }
}
